import React from 'react';
import { Box, Button, Divider, Stack, styled, Typography } from '@mui/material';
import ContainerBox from 'components/ContainerBox';
import { RiCheckLine } from 'react-icons/ri';
import { PlanDefinitions, SubscriptionPlan } from 'common/modules/plans';
import {
	getPlanKeyFeatures,
	getPlanPriceString,
	getPlanPaymentsPrice,
	getPlanDeliveryPrice,
	getPlanDescription,
} from 'services/plans/utils';
import { useTranslation } from 'services/localization';
import ExternalLink from 'components/ExternalLink';
import { getStripeCommission } from 'common/constants/paymentCommissions';
import { BILLING_CURRENCY } from 'common/modules/billing';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useSelector } from 'react-redux';

export interface PlanBlockProps {
	plan: SubscriptionPlan;
	duration: number;
	discount: number;
	active?: boolean;
	onPlanClick: (plan: SubscriptionPlan) => void;
}
const PlanBlock = (props: PlanBlockProps) => {
	const { plan, duration, discount, active, onPlanClick } = props;
	const { t } = useTranslation();
	const planDefinition = PlanDefinitions[plan];
	const shopPlan = useSelector(ShopSelectors.shopPlan)!;
	const shopFeatures = useSelector(ShopSelectors.shopFeatures);
	const hasCoreAdvancedDiscount =
		shopPlan === 'ADVANCED' &&
		plan === 'ADVANCED' &&
		shopFeatures?.plan.campaign === 'core_commission_to_advanced_with_grow_price';

	const renderDefaultPriceText = () => (
		<Typography variant="body1" textAlign="center" sx={{ mt: 2, fontSize: '1.4rem' }}>
			<Typography variant="h6" sx={{ display: 'inline', fontWeight: 500 }}>
				{getPlanPriceString(plan, BILLING_CURRENCY, discount)}
			</Typography>{' '}
			{t('common:times.perMonthPerStoreShort', {
				price: '',
				defaultValue: '{{price}} /mo /store',
			})}
		</Typography>
	);

	const renderCoreAdvancedDiscountText = () => (
		<>
			<Typography variant="body1" textAlign="center" sx={{ mt: 2, fontSize: '1.4rem' }}>
				<Typography
					variant="h6"
					sx={{ display: 'inline', fontWeight: 500 }}
					style={{ textDecoration: 'line-through' }}
				>
					{getPlanPriceString(plan, BILLING_CURRENCY)}
				</Typography>
				<Typography variant="h6" sx={{ display: 'inline', fontWeight: 500 }} pl={1}>
					{getPlanPriceString('GROW', BILLING_CURRENCY)}{' '}
				</Typography>{' '}
				{t('common:times.perMonthPerStoreShort', {
					price: '',
					defaultValue: '{{price}} /mo /store',
				})}
			</Typography>
			<Typography
				variant="body2"
				color="secondary"
				textAlign="center"
				fontWeight="bold"
				sx={{ fontSize: '1.2rem' }}
			>
				(
				{t(
					'account.billing.plan.coreAdvancedDiscount',
					'offer for Rentle Core customers until 8.6.2023',
				)}
				)
			</Typography>
		</>
	);

	return (
		<ContainerBox
			sx={{
				outline: (theme) => (active ? `1px solid ${theme.palette.primary.dark}` : undefined),
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				position: 'relative',
			}}
		>
			{active && (
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="center"
					sx={{
						position: 'absolute',
						top: -12,
						left: 0,
						right: 0,
					}}
				>
					<Box
						sx={{
							height: 24,
							padding: (theme) => theme.spacing(0, 1),
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							borderRadius: 16,
							background: (theme) => theme.palette.primary.dark,
							color: (theme) => theme.palette.primary.contrastText,
						}}
					>
						{t('common:times.current', 'Current')}
					</Box>
				</Stack>
			)}
			<Section>
				<Typography variant="h6" textAlign="center" sx={{ fontSize: '2.2rem' }}>
					{planDefinition.name}
				</Typography>
				<Typography variant="body1" color="secondary" textAlign="center" sx={{ mt: 3 }}>
					{getPlanDescription(plan, t)}
				</Typography>
				{hasCoreAdvancedDiscount ? renderCoreAdvancedDiscountText() : renderDefaultPriceText()}
			</Section>
			<Button
				variant="contained"
				fullWidth
				disabled={active}
				sx={{
					mb: 2,
					maxWidth: 300,
					alignSelf: 'center',
					backgroundColor: (theme) => theme.palette.primary.dark,
				}}
				onClick={() => onPlanClick(plan)}
			>
				{duration === 1
					? t('plans.planBlocks.choosePlanCTA', 'Choose plan')
					: t('plans.planBlocks.getYearlyQuota', 'Get yearly quota')}
			</Button>
			<Divider />
			<Section flex={1} sx={{ pb: 0 }}>
				<SectionHeader>{t('plans.planBlocks.keyFeaturesTitle', 'Key Features')}</SectionHeader>
				{getPlanKeyFeatures(plan, t)
					.filter((f) => !f.hideInPreview)
					.map((feature) => (
						<Stack direction="row" alignItems="center" sx={{ mb: 0.5 }} key={feature.title}>
							<RiCheckLine size={20} />
							<Typography color="secondary" sx={{ ml: 1, fontSize: '1.4rem' }}>
								{feature.title}
							</Typography>
						</Stack>
					))}

				<ExternalLink
					sx={{
						textDecoration: 'none',
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						mt: 1,
					}}
					href={'https://rentle.io/pricing'}
				>
					<Typography sx={{ fontSize: '1.4rem' }}>
						{t('plans.planBlocks.allFeatures', 'Show all features')}
					</Typography>
				</ExternalLink>
			</Section>
			<Divider />
			<Section>
				<SectionHeader>
					{t('plans.planBlocks.merchantSolutiosTitle', 'Merchant Solutions')}
				</SectionHeader>
				<MerchantSolutionItem
					label={t('plans.planBlocks.merchantSolution1', 'Rentle Payments')}
					price={getPlanPaymentsPrice(plan)}
				/>
				<MerchantSolutionItem
					label={t('plans.planBlocks.merchantSolution2', 'Delivery Platform')}
					price={getPlanDeliveryPrice(plan, t)}
				/>
				<MerchantSolutionItem
					label={t('plans.planBlocks.merchantSolution3', '3rd party payments (e.g. Stripe)')}
					price={getStripeCommission(plan)}
				/>
			</Section>
		</ContainerBox>
	);
};

const Section = styled(Box)(({ theme }) => ({
	padding: theme.spacing(3, 0),
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
	fontSize: '1.4rem',
	fontWeight: 500,
	marginBottom: theme.spacing(2),
}));

const MerchantSolutionItem = ({ label, price }: { label: string; price: string }) => {
	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'row',
				alignItems: 'center',
				justifyContent: 'space-between',
			}}
		>
			<Typography color="secondary" sx={{ fontSize: '1.4rem' }}>
				{label}
			</Typography>
			<Typography color="secondary" sx={{ fontSize: '1.4rem' }} textAlign="right">
				{price}
			</Typography>
		</Box>
	);
};

export default PlanBlock;
